import React from 'react';
import { Redirect, Route, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
  hasViewGroupPerformanceReport,
  hasViewHistoricalOnCall,
  hasViewWhosOnDuty,
} from 'selectors/permissions';
import { isReactDynamicTeamDetailsPageEnabled } from 'selectors/featureToggles';
import useFeatureToggle from 'hooks/useFeatureToggle';
import lazyLoad from 'utils/lazyLoad';
import { getPathsForGwtPlace, getRouterPath } from 'utils/navigation';
import { RemoveTrailingSlashRedirect } from './utils';
import { GroupListRedirect } from './GroupsRedirects';

const WhosOnDuty = lazyLoad(
  () => import(/* webpackChunkName: "WhosOnDuty" */ 'pages/WhosOnDuty'),
);
const GroupListPage = lazyLoad(
  () => import(/* webpackChunkName: "GroupListPage" */ 'pages/GroupListPage'),
);
const DynamicTeamDetailsPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "DynamicTeamDetailsPage" */ 'pages/DynamicTeamDetailsPage'
    ),
);
const GroupDetailsPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "GroupDetailsPage" */ 'pages/GroupDetails/GroupDetailsPage'
    ),
);
const DynamicTeamsListPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "DynamicTeamsListPage" */ 'pages/DynamicTeamsListPage'
    ),
);
const PerformanceReportContainer = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "PerformanceReportContainer" */ 'pages/Reports/PerformanceReports/PerformanceReportContainer'
    ),
);
const HistoricalOnCall = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "HistoricalOnCall" */ 'pages/Reports/HistoricalOnCall'
    ),
);
const UnauthorizedPageAccess = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "UnauthorizedPageAccess" */ 'pages/UnauthorizedPageAccess'
    ),
);

function useGroupDetailsRedirectPath() {
  const { groupId, tab, subTab, subId } = useParams();
  let path = `/groups/${groupId}/${tab || 'overview'}`;

  if (!subTab) {
    return path;
  }
  path = `${path}/${subTab}`;

  if (!subId) {
    return path;
  }
  path = `${path}/${subId}`;

  return path;
}

export function GroupDetailsRedirect() {
  // Handle links to legacy URLs:
  return <Redirect to={getRouterPath(useGroupDetailsRedirectPath())} />;
}

export default function useGroupsRoutes() {
  const reactDynamicTeamDetailsPageEnabled = useSelector(
    isReactDynamicTeamDetailsPageEnabled,
  );
  const canViewGroupPerformanceReport = useSelector(
    hasViewGroupPerformanceReport,
  );
  const canViewHistoricalOnCall = useSelector(hasViewHistoricalOnCall);
  const canViewWhosOnDuty = useSelector(hasViewWhosOnDuty);
  const isGroupUserIncidentPerformanceEnabled = useFeatureToggle(
    'GROUP_USER_INCIDENT_PERFORMANCE',
  );
  const isIncidentManagementEnabled = useFeatureToggle(
    'INCIDENT_MANAGEMENT_MVP',
  );

  return [
    // Sanitize URLs with trailing spaces via `strict` prop: https://v5.reactrouter.com/web/api/Route/strict-bool
    <Route
      strict
      exact
      path={[
        getRouterPath('/groups/'),
        getRouterPath('/groups/:groupId/'),
        getRouterPath('/groups/:groupId/(.+)/'),
      ]}
      component={RemoveTrailingSlashRedirect}
    />,
    <Route
      path={getRouterPath('/WhosOnDutyGroupSelectionPlace:')}
      component={canViewWhosOnDuty ? WhosOnDuty : UnauthorizedPageAccess}
    />,
    <Route
      exact
      path={[
        getRouterPath('/groups'),
        // Deal with legacy URL params from old GWT days:
        getRouterPath('/groups([:/]none)'),
        getRouterPath('/groups([:/]filters=.+)'),
      ]}
      component={GroupListPage}
    />,
    <Route
      path={getRouterPath('/GroupsPlace:(.+)?')}
      component={GroupListRedirect}
    />,
    <Route
      path={getRouterPath('/DynamicTeams')}
      component={DynamicTeamsListPage}
    />,
    reactDynamicTeamDetailsPageEnabled && (
      <Route
        path={getRouterPath('/dynamic-teams/:teamId?/:tab?/:subTab?')}
        component={DynamicTeamDetailsPage}
      />
    ),
    <Route
      path={getRouterPath('/groups/:groupId/:tab/:subTab?/:subId?')}
      component={GroupDetailsPage}
    />,
    // Handle legacy URLs:
    <Route
      exact
      path={[
        getRouterPath('/OnCallGroupDetails/:groupId?/:tab?/:subTab?/:subId?'),
        getRouterPath('/BroadcastGroupDetails/:groupId?/:tab?/:subTab?'),
        getRouterPath('/DynamicGroupDetails/:groupId?/:tab?/:subTab?'),
        // Not legacy, but will automatically add the tab:
        getRouterPath('/groups/:groupId'),
      ]}
      component={GroupDetailsRedirect}
    />,
    <Route
      path={[
        getRouterPath(
          '/GroupPerformance/alerts/:supervisors?/:parentGroup?/:recipients?/:members?/:startDate?/:endDate?/:drilldownSource?',
        ),
        getRouterPath('/GroupPerformance*'),
      ]}
      component={props =>
        canViewGroupPerformanceReport ? (
          <PerformanceReportContainer
            recipientType="Group"
            isGroupUserIncidentPerformanceEnabled={
              isGroupUserIncidentPerformanceEnabled
            }
            isIncidentManagementEnabled={isIncidentManagementEnabled}
            {...props}
          />
        ) : (
          <UnauthorizedPageAccess />
        )
      }
    />,
    <Route
      path={getPathsForGwtPlace('historicalOnCall')}
      component={
        canViewHistoricalOnCall ? HistoricalOnCall : UnauthorizedPageAccess
      }
    />,
  ];
}
